import { useDispatch } from 'react-redux';
import { SectionImage } from 'services/api/vehicle-sections/types';
import styled from 'styled-components';
import { openFullScreenWithId } from 'services/viewVehicleSlice/viewVehicleSlice';
import { useEffect, useRef, useState } from 'react';
import { chunkByN } from 'utils/array';
import { AIOverviewIcon } from 'components/AiOverview';
import AutoPlayVideo from 'components/AutoPlayVideo';
import moment from 'moment';

const Wrapper = styled.div`
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #fff;
  border-radius: 8px;
`;

const Header = styled.div`
  font-weight: 700;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  color: #000;
`;

const ImageFlex = styled.div<{ isExpanded: boolean }>`
  display: flex;
  gap: 8px;
  overflow-x: ${props => (props.isExpanded ? 'auto' : 'hidden')};
`;

const ImageGrid = styled.div<{ layoutSize: string; numberOfItems: number }>`
  display: grid;
  grid-gap: 4px;
  grid-template-columns: ${props => (props.layoutSize === 'lg' ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)')};
  grid-template-rows: ${props => (props.numberOfItems > 6 ? 'repeat(3, 1fr)' : 'auto')};
  flex: ${props => (props.layoutSize === 'lg' ? '0 1 auto' : '0 0 100%')};
`;

const Image = styled.div<{ url: string; imageToExpand: boolean }>`
  background-image: ${({ url }) => `url(${url})`};
  background-size: contain;
  background-repeat: no-repeat;
  aspect-ratio: 4 / 3;
  display: flex;
  position: relative;
  align-items: flex-end;
  opacity: ${props => (props.imageToExpand ? '0.7' : 1)};
`;

const Video = styled.video`
  width: 100%;
  margin-top: -10px;
`;

const NotesWrapper = styled.div`
  width: 100%;
  max-height: 60%;
  overflow: hidden;

  column-width: 100%;
  width: 100%;

  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;

  display: flex;
  padding: 12px 5px 0 8px;

  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.6) 100%);

  &:hover {
    min-height: 100%;
    max-height: 100%;

    & > span {
      -webkit-line-clamp: 7;
      padding-top: 4px;
    }
  }

  &::after {
    content: '';
    display: block;
    min-height: 13px;
    flex-shrink: 0;
  }
`;

const Notes = styled.span`
  font-weight: 500;
  font-size: 10px;
  color: #fff;
  overflow: hidden;
  column-width: 100%;
  width: 100%;

  display: -webkit-box;
  -webkit-line-clamp: 3; /* number of visible lines */
  -webkit-box-orient: vertical;

  line-height: 14px;
`;

type Props = {
  images: SectionImage[];
  label: string;
  layoutSize?: 'md' | 'lg';
  video?: string;
};

const PlusNLabel = styled.div`
  font-weight: 600;
  font-size: 24px;
  line-height: 100%;
  color: #fff;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

export const ImageList = ({ images, label, video, layoutSize = 'md' }: Props) => {
  if (images.length === 0) return null;

  return (
    <Wrapper>
      <Header>{label}</Header>
      {layoutSize === 'md' ? (
        <DefaultImageLayout video={video} images={images} />
      ) : (
        <LargeImageLayout images={images} />
      )}
    </Wrapper>
  );
};

type ImageLayoutProps = {
  images: SectionImage[];
  video?: string;
};

const LargeImageLayout = ({ images }: ImageLayoutProps) => {
  const dispatch = useDispatch();
  return (
    <ImageGrid numberOfItems={images.length} layoutSize={'lg'}>
      {images.map((img: any, j) => {
        const handleClick = () => {
          dispatch(openFullScreenWithId(img.id));
        };
        return (
          <Image
            id={`vehicle-view-image-${img.id}`}
            imageToExpand={false}
            key={img.id}
            url={img.url_thumb}
            onClick={handleClick}
          >
            <ImageNotesComponent img={img} />
          </Image>
        );
      })}
    </ImageGrid>
  );
};

const DefaultImageLayout = ({ images, video }: ImageLayoutProps) => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || !isExpanded) {
      return;
    }

    containerRef.current.scrollTo({
      left: 1 * containerRef.current.clientWidth,
      behavior: 'smooth'
    });
  }, [isExpanded]);

  const imageMatrix = chunkByN(images, 6);

  return (
    <>
      <ImageFlex ref={containerRef} isExpanded={isExpanded}>
        {imageMatrix.map((imageChunk, i) => (
          <ImageGrid key={i} numberOfItems={images.length} layoutSize={'md'}>
            {imageChunk.map((img: any, j) => {
              const imageToExpand = i === 0 && j === 5 && images.length > 6 && !isExpanded;
              const handleClick = () => {
                if (imageToExpand) {
                  return setIsExpanded(true);
                }

                dispatch(openFullScreenWithId(img.id));
              };
              const showExpiredBanner = img.expiration_date && moment().isAfter(img.expiration_date);
              return (
                <Image
                  id={`vehicle-view-image-${img.id}`}
                  imageToExpand={imageToExpand}
                  key={img.id}
                  url={img.url_thumb}
                  onClick={handleClick}
                >
                  {showExpiredBanner && <ExpiredLabel />}
                  <ImageNotesComponent img={img} />
                  {imageToExpand && <PlusNLabel>{`+${images.length - 6}`}</PlusNLabel>}
                </Image>
              );
            })}
          </ImageGrid>
        ))}
      </ImageFlex>
      {video && <AutoPlayVideo style={{ marginTop: '-10px', height: 'inherit' }} src={video} />}
    </>
  );
};

const ImageNotesComponent = ({ img }: { img: SectionImage }) => {
  if (!img.notes || !img.notes.message) return null;
  return (
    <NotesWrapper>
      {img.notes?.ai_description && <AIOverviewIcon style={{ marginLeft: '-8px' }} text={img.notes.ai_description} />}
      <Notes>{img.notes.message}</Notes>
    </NotesWrapper>
  );
};

const ExpiredLabelWrapper = styled.div`
  background: #d01c2e;
  border-top-left-radius: 100px;
  border-bottom-left-radius: 100px;
  padding: 0 6px 0 7px;

  font-size: 10px;
  line-height: 24px;
  color: #fff;
  position: absolute;
  right: 0;
  top: 10px;
`;

const ExpiredLabel = () => {
  return <ExpiredLabelWrapper>Expired</ExpiredLabelWrapper>;
};
